import { createAsyncThunk } from "@reduxjs/toolkit";
import { type Dayjs } from "dayjs";

import { type ClientInvoiceData, type Order } from "@fitness-app/data-models/entities/Order";
import { getErrorMessage } from "@fitness-app/utils";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type ActionParams, type AsyncThunkCreator } from "../../../index";
import { PRODUCT_CLIENT_REDUCER } from "../types";

type Payload = {
  invoiceData: ClientInvoiceData;
  clientEmail?: string;
  updateClientData?: boolean;
  orderId?: string;
  sellDate?: Dayjs;
  issueDate?: Dayjs;
};

export const updateInvoiceData = createAsyncThunk<void, ActionParams<Payload>, AsyncThunkCreator<string>>(
  `${PRODUCT_CLIENT_REDUCER}/updateInvoiceData`,
  async ({ payload, onSuccess, onFailure }, { rejectWithValue, extra: { db, auth } }) => {
    try {
      const { email } = await getLoggedUser(auth);

      // The admin panel edits somebody else's row, so the target is
      // payload.clientEmail — it was declared here but never read, which meant
      // admin edits always addressed the logged-in trainer's own e-mail,
      // matched nothing and reported success (TRE-2143). client-web passes the
      // client's own address, so the logged-in fallback keeps it working.
      const clientEmail = payload.clientEmail ?? email;

      // The order goes FIRST. It is what the invoice is issued from — the
      // service reads order.client for the buyer and order.sellDate/issueDate
      // for the document dates — so it must not depend on the saved client row
      // being there. An order can outlive (or precede) its product_client: an
      // import, a failed sign-up, an address that never matched (TRE-1668).
      if (payload.orderId) {
        const orderToUpdate: Partial<Order> = {
          client: payload.invoiceData,
        };

        if (payload.issueDate) {
          orderToUpdate.issueDate = payload.issueDate.unix();
        }

        if (payload.sellDate) {
          orderToUpdate.sellDate = payload.sellDate.unix();
        }

        const { data, error } = await db
          .from("product_order")
          .update(orderToUpdate)
          .eq("id", payload.orderId)
          .select("id");

        if (error) {
          onFailure?.();

          return rejectWithValue(error.message);
        }

        if (!data.length) {
          onFailure?.();

          return rejectWithValue(`No product order matched ${payload.orderId}`);
        }
      }

      // `updateClientData === false` comes from the issue-invoice form and
      // means: put the data on this one invoice, leave the saved client data
      // alone. Only that form sends the flag.
      if (payload.updateClientData !== false) {
        const { data, error } = await db
          .from("product_client")
          .update({
            client: payload.invoiceData,
          })
          .eq("email", clientEmail)
          .select("id");

        if (error) {
          onFailure?.();

          return rejectWithValue(error.message);
        }

        // No row for that address is only a failure when the saved client data
        // was the whole point of the call. Alongside an order it is an expected
        // shape, and the service skips the same sync with a warning rather than
        // failing the issue (TRE-1770) — the invoice is already safe either way,
        // because the order above carries the data it is built from.
        if (!data.length && !payload.orderId) {
          onFailure?.();

          return rejectWithValue(`No product client row matched ${clientEmail}`);
        }
      }

      onSuccess?.();
    } catch (e) {
      onFailure?.();

      return rejectWithValue(getErrorMessage(e));
    }
  },
);
